import React, { useEffect, useMemo, useState } from 'react';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import {
    assignImageToGallery,
    deleteImage,
    fetchAllGalleriesAdmin,
    fetchHomeBlocksAdmin,
    fetchImageById,
    regenerateAiTextsForImage,
    saveImagePrintOptions,
    updateImageMeta,
} from '../services/api';
import { Gallery, HomeBlock, Image, PrintOption } from '../types';

type RouteParams = {
    id: string;
};

const defaultAiTexts = {
    da: { concise: '', expert: '', dreamy: '', hashtags: '' },
    en: { concise: '', expert: '', dreamy: '', hashtags: '' },
};

const createEmptyPrintOption = (sortOrder: number): PrintOption => ({
    label: '',
    priceCents: 0,
    currency: 'dkk',
    isActive: true,
    sortOrder,
});

const AdminImageEdit: React.FC = () => {
    const { id } = useParams<RouteParams>();
    const history = useHistory();
    const location = useLocation();
    const [image, setImage] = useState<Image | null>(null);
    const [galleries, setGalleries] = useState<Gallery[]>([]);
    const [homeBlocks, setHomeBlocks] = useState<HomeBlock[]>([]);
    const [selectedHomeBlockIds, setSelectedHomeBlockIds] = useState<number[]>([]);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [showOnHomepage, setShowOnHomepage] = useState(false);
    const [showAsVision, setShowAsVision] = useState(false);
    const [galleryId, setGalleryId] = useState<string | ''>('');
    const [aiTexts, setAiTexts] = useState<Image['aiTexts']>(defaultAiTexts);
    const [printOptions, setPrintOptions] = useState<PrintOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [regenerating, setRegenerating] = useState(false);
    const [copiedField, setCopiedField] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const returnTo = useMemo(() => {
        const params = new URLSearchParams(location.search);
        const candidate = params.get('returnTo') || '';
        if (candidate.startsWith('/admin')) {
            return candidate;
        }
        return '/admin#images';
    }, [location.search]);

    const loadData = async () => {
        try {
            const [imageData, galleriesData, homeBlocksData] = await Promise.all([
                fetchImageById(id),
                fetchAllGalleriesAdmin(),
                fetchHomeBlocksAdmin(),
            ]);
            setImage(imageData);
            setGalleries(galleriesData);
            setHomeBlocks(homeBlocksData || []);
            setTitle(imageData.title || '');
            setDescription(imageData.description || '');
            setShowOnHomepage(Boolean(imageData.showOnHomepage));
            setShowAsVision(Boolean(imageData.showAsVision));
            setGalleryId(imageData.galleryId || '');
            setSelectedHomeBlockIds(
                Array.isArray(imageData.homeBlockIds)
                    ? imageData.homeBlockIds.map((value: number | string) => Number(value)).filter((value: number) => Number.isFinite(value))
                    : []
            );
            setAiTexts(
                imageData.aiTexts || {
                    da: {
                        concise: imageData.aiTextDaConcise || '',
                        expert: imageData.aiTextDaExpert || '',
                        dreamy: imageData.aiTextDaDreamy || '',
                        hashtags: imageData.aiTextDaHashtags || '',
                    },
                    en: {
                        concise: imageData.aiTextEnConcise || '',
                        expert: imageData.aiTextEnExpert || '',
                        dreamy: imageData.aiTextEnDreamy || '',
                        hashtags: imageData.aiTextEnHashtags || '',
                    },
                }
            );
            setPrintOptions(Array.isArray(imageData.printOptions) ? imageData.printOptions : []);
        } catch (e) {
            setError('Kunne ikke hente billedet.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, [id]);

    const galleryTitleById = useMemo(() => {
        return galleries.reduce<Record<string, string>>((acc, g) => {
            acc[g.id] = g.title;
            return acc;
        }, {});
    }, [galleries]);

    const handleSave = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!image) return;
        setSaving(true);
        setError(null);
        try {
            await updateImageMeta(image.id, {
                title,
                description,
                showOnHomepage,
                showAsVision,
                aiTextDaConcise: aiTexts?.da?.concise,
                aiTextDaExpert: aiTexts?.da?.expert,
                aiTextDaDreamy: aiTexts?.da?.dreamy,
                aiTextDaHashtags: aiTexts?.da?.hashtags,
                aiTextEnConcise: aiTexts?.en?.concise,
                aiTextEnExpert: aiTexts?.en?.expert,
                aiTextEnDreamy: aiTexts?.en?.dreamy,
                aiTextEnHashtags: aiTexts?.en?.hashtags,
                homeBlockIds: selectedHomeBlockIds,
            });
            await assignImageToGallery(image.id, galleryId || null);
            await saveImagePrintOptions(
                image.id,
                printOptions
                    .map((option, index) => ({
                        ...option,
                        label: option.label.trim(),
                        currency: (option.currency || 'dkk').trim().toLowerCase(),
                        priceCents: Math.round(Number(option.priceCents)),
                        sortOrder: Number.isFinite(Number(option.sortOrder)) ? Number(option.sortOrder) : index,
                    }))
                    .filter((option) => option.label || option.priceCents > 0)
            );
            await loadData();
        } catch (e) {
            setError('Kunne ikke gemme ændringer.');
        } finally {
            setSaving(false);
        }
    };

    const handleRegenerateAi = async () => {
        if (!image) return;
        setError(null);
        setRegenerating(true);
        try {
            const data = await regenerateAiTextsForImage(image.id);
            if (data?.image?.aiTexts) {
                setImage(data.image);
                setAiTexts(data.image.aiTexts);
                setTitle(data.image.title || '');
                setDescription(data.image.description || '');
            }
        } catch (e) {
            setError('Kunne ikke regenerere AI-tekster.');
        } finally {
            setRegenerating(false);
        }
    };

    const handleDelete = async () => {
        if (!image) return;
        if (!window.confirm('Vil du slette billedet?')) return;
        try {
            await deleteImage(image.id);
            history.push(returnTo);
        } catch (e) {
            setError('Kunne ikke slette billedet.');
        }
    };

    const handleCopy = async (key: string, text: string) => {
        if (!text.trim()) return;
        try {
            await navigator.clipboard.writeText(text);
            setCopiedField(key);
            window.setTimeout(() => {
                setCopiedField((current) => (current === key ? null : current));
            }, 1600);
        } catch (e) {
            setError('Kunne ikke kopiere hashtags.');
        }
    };

    const toggleHomeBlock = (blockId: number, checked: boolean) => {
        setSelectedHomeBlockIds((current) => {
            if (checked) {
                return Array.from(new Set([...current, blockId]));
            }
            return current.filter((id) => id !== blockId);
        });
    };

    const updatePrintOption = (index: number, key: string, value: string | number | boolean) => {
        setPrintOptions((current) =>
            current.map((option, optionIndex) =>
                optionIndex === index ? { ...option, [key]: value } : option
            )
        );
    };

    const addPrintOption = () => {
        setPrintOptions((current) => [...current, createEmptyPrintOption(current.length)]);
    };

    const removePrintOption = (index: number) => {
        setPrintOptions((current) => current.filter((_, optionIndex) => optionIndex !== index));
    };

    if (loading) {
        return <div>Indlæser...</div>;
    }

    if (!image) {
        return <div>{error || 'Billede ikke fundet.'}</div>;
    }

    return (
        <div className="admin-image-edit">
            <div className="admin-image-edit__header">
                <div>
                    <h1>Rediger billede</h1>
                    <p className="gallery-list-meta">
                        {image.filename ? `Fil: ${image.filename}` : 'Fil: —'} ·{' '}
                        {galleryTitleById[image.galleryId] || 'Ingen galleri'}
                    </p>
                </div>
                <div className="admin-actions">
                    <Link className="link-button" to={returnTo}>
                        Tilbage
                    </Link>
                    <button type="button" className="link-button link-button--danger" onClick={handleDelete}>
                        Slet
                    </button>
                </div>
            </div>

            {error && <div className="error">{error}</div>}

            <div className="admin-image-edit__layout">
                <div className="admin-image-edit__preview">
                    <img src={image.url || image.thumbnailUrl} alt={image.title || 'billede'} />
                </div>
                <form className="admin-image-edit__form" onSubmit={handleSave}>
                    <label>
                        <span>Titel</span>
                        <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} />
                    </label>
                    <label>
                        <span>Beskrivelse</span>
                        <textarea value={description} onChange={(e) => setDescription(e.target.value)} />
                    </label>
                    <label>
                        <span>Galleri</span>
                        <select value={galleryId} onChange={(e) => setGalleryId(e.target.value)}>
                            <option value="">Ingen galleri</option>
                            {galleries.map((g) => (
                                <option key={g.id} value={g.id}>
                                    {g.title}
                                </option>
                            ))}
                        </select>
                    </label>
                    <fieldset className="admin-image-edit__blocks admin-image-edit__print-options">
                        <legend>Printpriser</legend>
                        {!printOptions.length ? (
                            <p className="gallery-list-meta">Ingen printpriser endnu.</p>
                        ) : (
                            printOptions.map((option, index) => (
                                <div key={option.id || `new-${index}`} className="print-option-row">
                                    <label>
                                        <span>Størrelse / label</span>
                                        <input
                                            type="text"
                                            value={option.label}
                                            placeholder="A3 fine art print"
                                            onChange={(event) => updatePrintOption(index, 'label', event.target.value)}
                                        />
                                    </label>
                                    <label>
                                        <span>Pris</span>
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={option.priceCents ? option.priceCents / 100 : ''}
                                            placeholder="1200"
                                            onChange={(event) =>
                                                updatePrintOption(index, 'priceCents', Math.round(Number(event.target.value || 0) * 100))
                                            }
                                        />
                                    </label>
                                    <label>
                                        <span>Valuta</span>
                                        <input
                                            type="text"
                                            value={option.currency || 'dkk'}
                                            maxLength={3}
                                            onChange={(event) => updatePrintOption(index, 'currency', event.target.value.toLowerCase())}
                                        />
                                    </label>
                                    <label className="checkbox-row print-option-row__active">
                                        <input
                                            type="checkbox"
                                            checked={option.isActive !== false}
                                            onChange={(event) => updatePrintOption(index, 'isActive', event.target.checked)}
                                        />
                                        Aktiv
                                    </label>
                                    <button type="button" className="link-button link-button--danger" onClick={() => removePrintOption(index)}>
                                        Fjern
                                    </button>
                                </div>
                            ))
                        )}
                        <button type="button" className="link-button" onClick={addPrintOption}>
                            Tilføj printpris
                        </button>
                    </fieldset>
                    <label className="checkbox-row">
                        <input
                            type="checkbox"
                            checked={showOnHomepage}
                            onChange={(e) => setShowOnHomepage(e.target.checked)}
                        />
                        Brug som hero på forsiden
                    </label>
                    <p className="gallery-list-meta">
                        Der kan kun være ét hero-billede. Vælger du dette, fjernes markeringen fra det nuværende hero-billede.
                    </p>
                    <label className="checkbox-row">
                        <input
                            type="checkbox"
                            checked={showAsVision}
                            onChange={(e) => setShowAsVision(e.target.checked)}
                        />
                        Brug som vision-billede
                    </label>
                    <p className="gallery-list-meta">
                        Der kan kun være ét vision-billede. Vælger du dette, fjernes markeringen fra det nuværende vision-billede.
                    </p>
                    <fieldset className="admin-image-edit__blocks">
                        <legend>Forsideblokke</legend>
                        {!homeBlocks.length ? (
                            <p className="gallery-list-meta">Ingen blokke fundet.</p>
                        ) : (
                            homeBlocks.map((block) => (
                                <label key={block.id} className="checkbox-row">
                                    <input
                                        type="checkbox"
                                        checked={selectedHomeBlockIds.includes(block.id)}
                                        onChange={(event) => toggleHomeBlock(block.id, event.target.checked)}
                                    />
                                    {block.title || block.key}
                                </label>
                            ))
                        )}
                    </fieldset>
                    <div className="modal-actions">
                        <button type="button" onClick={handleRegenerateAi}>
                            {regenerating ? (
                                <span className="spinner" aria-label="Regenererer" />
                            ) : (
                                'Regenerer AI-tekster'
                            )}
                        </button>
                        <button type="submit" disabled={saving}>
                            {saving ? 'Gemmer...' : 'Gem'}
                        </button>
                    </div>
                </form>
            </div>

            <section className="ai-texts ai-texts--page">
                <div>
                    <strong>AI-tekster (DA)</strong>
                    <p className="ai-texts__label">Kort</p>
                    <textarea
                        className="ai-texts__area"
                        value={aiTexts?.da?.concise || ''}
                        onChange={(e) =>
                            setAiTexts((prev) => ({
                                ...(prev || defaultAiTexts),
                                da: {
                                    ...(prev?.da || defaultAiTexts.da),
                                    concise: e.target.value,
                                },
                            }))
                        }
                    />
                    <p className="ai-texts__label">Ekspert</p>
                    <textarea
                        className="ai-texts__area"
                        value={aiTexts?.da?.expert || ''}
                        onChange={(e) =>
                            setAiTexts((prev) => ({
                                ...(prev || defaultAiTexts),
                                da: {
                                    ...(prev?.da || defaultAiTexts.da),
                                    expert: e.target.value,
                                },
                            }))
                        }
                    />
                    <p className="ai-texts__label">Drømmende</p>
                    <textarea
                        className="ai-texts__area"
                        value={aiTexts?.da?.dreamy || ''}
                        onChange={(e) =>
                            setAiTexts((prev) => ({
                                ...(prev || defaultAiTexts),
                                da: {
                                    ...(prev?.da || defaultAiTexts.da),
                                    dreamy: e.target.value,
                                },
                            }))
                        }
                    />
                    <div className="ai-texts__row">
                        <p className="ai-texts__label">Hashtags</p>
                        <button type="button" className="link-button" onClick={() => handleCopy('da-hashtags', aiTexts?.da?.hashtags || '')}>
                            {copiedField === 'da-hashtags' ? 'Kopieret' : 'Kopier'}
                        </button>
                    </div>
                    <textarea
                        className="ai-texts__area ai-texts__area--hashtags"
                        value={aiTexts?.da?.hashtags || ''}
                        onChange={(e) =>
                            setAiTexts((prev) => ({
                                ...(prev || defaultAiTexts),
                                da: {
                                    ...(prev?.da || defaultAiTexts.da),
                                    hashtags: e.target.value,
                                },
                            }))
                        }
                    />
                </div>
                <div>
                    <strong>AI-tekster (EN)</strong>
                    <p className="ai-texts__label">Short</p>
                    <textarea
                        className="ai-texts__area"
                        value={aiTexts?.en?.concise || ''}
                        onChange={(e) =>
                            setAiTexts((prev) => ({
                                ...(prev || defaultAiTexts),
                                en: {
                                    ...(prev?.en || defaultAiTexts.en),
                                    concise: e.target.value,
                                },
                            }))
                        }
                    />
                    <p className="ai-texts__label">Expert</p>
                    <textarea
                        className="ai-texts__area"
                        value={aiTexts?.en?.expert || ''}
                        onChange={(e) =>
                            setAiTexts((prev) => ({
                                ...(prev || defaultAiTexts),
                                en: {
                                    ...(prev?.en || defaultAiTexts.en),
                                    expert: e.target.value,
                                },
                            }))
                        }
                    />
                    <p className="ai-texts__label">Dreamy</p>
                    <textarea
                        className="ai-texts__area"
                        value={aiTexts?.en?.dreamy || ''}
                        onChange={(e) =>
                            setAiTexts((prev) => ({
                                ...(prev || defaultAiTexts),
                                en: {
                                    ...(prev?.en || defaultAiTexts.en),
                                    dreamy: e.target.value,
                                },
                            }))
                        }
                    />
                    <div className="ai-texts__row">
                        <p className="ai-texts__label">Hashtags</p>
                        <button type="button" className="link-button" onClick={() => handleCopy('en-hashtags', aiTexts?.en?.hashtags || '')}>
                            {copiedField === 'en-hashtags' ? 'Copied' : 'Copy'}
                        </button>
                    </div>
                    <textarea
                        className="ai-texts__area ai-texts__area--hashtags"
                        value={aiTexts?.en?.hashtags || ''}
                        onChange={(e) =>
                            setAiTexts((prev) => ({
                                ...(prev || defaultAiTexts),
                                en: {
                                    ...(prev?.en || defaultAiTexts.en),
                                    hashtags: e.target.value,
                                },
                            }))
                        }
                    />
                </div>
            </section>
        </div>
    );
};

export default AdminImageEdit;
